import React from 'react';
import { Bookmark, BookmarksQueueEntry, QueueEntryStates } from '../../../functions/src/types';
import { bulkUpdateBookmarkTags, formatApiError, getAllBookmarks, getAllTags, stash, updateExistingBookmark } from '../ApiClient';
import { debuglog } from '../Log';
import { extractUrlFromText } from '../Utils';
import { useFeedbackContext } from './FeedbackContext';
import { useUIContext } from './UIContext';
import { UserState, useUserContext } from './UserContext';

// local storage key for the queue of bookmarks to save
const QUEUE_KEY = 'stashsite.queue';

enum QueueProcessingStates {
  INITIAL = 'INITIAL',
  LOADING_FROM_STORAGE = 'LOADING_FROM_STORAGE',
  QUEUE_LOADED = 'QUEUE_LOADED',
  PROCESSING_QUEUE = 'PROCESSING_QUEUE',
  FINISHED_PROCESSING = 'FINISHED_PROCESSING',
  ADDING_ENTRY = 'ADDING_ENTRY',
  SAVING_QUEUE_TO_STORAGE = 'SAVING_QUEUE_TO_STORAGE',
  IDLE = 'IDLE'
}


interface DataContextProps {
  allBookmarks: Bookmark[] | null;
  setAllBookmarks: (bookmarks: Bookmark[] | null) => void;
  removeBookmark: (url: string) => void;
  saveBookMark: (urlToSave: string) => void;
  saveModifiedBookmark: (bookmarkToSave: Bookmark) => void;
  queueBookmarkForSaving: (url: string) => void;
  queueEntries: BookmarksQueueEntry[];
  dequeueBookmark: (url: string) => void;
  loadMoreBookmarks: () => Promise<void>;
  hasMoreBookmarks: boolean;
  isLoadingMore: boolean;
  allTags: string[];
  refreshTags: () => Promise<void>;
  saveBulkModifiedBookmarks: (bookmarkIds: string[], addTags: string[], removeTags: string[]) => Promise<boolean>;
}

const DataContext = React.createContext<DataContextProps>({
  allBookmarks: null,
  setAllBookmarks: () => { },
  removeBookmark: () => { },
  saveBookMark: () => { },
  saveModifiedBookmark: () => { },
  queueBookmarkForSaving: () => { },
  queueEntries: [],
  dequeueBookmark: () => { },
  loadMoreBookmarks: async () => { },
  hasMoreBookmarks: false,
  isLoadingMore: false,
  allTags: [],
  refreshTags: async () => { },
  saveBulkModifiedBookmarks: async () => false,
});

export const useDataContext = (): DataContextProps =>
  React.useContext(DataContext);

interface DataProviderProps {
  children: React.ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({
  children,
}: DataProviderProps) => {
  const { checkToken, userState, user } = useUserContext();
  const { showErrorMessage, showMessage } = useFeedbackContext();
  const {
    targetSearch, targetTags, setLoadedSearch, setLoadedTags,
    targetNotConsistent, loadingBookmarks, setLoadingBookmarks,
    setSaving
  } = useUIContext();


  const [allBookmarks, setAllBookmarks] = React.useState<Bookmark[] | null>(null);
  const [queueEntries, setQueueEntries] = React.useState<BookmarksQueueEntry[]>([]);
  const [queueProcessingState, setQueueProcessingState] = React.useState<QueueProcessingStates>(QueueProcessingStates.INITIAL);
  const isProcessingQueueRef = React.useRef(false);
  const processingQueueRef = React.useRef<Set<string>>(new Set());
  const [nextToken, setNextToken] = React.useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [currentSearch, setCurrentSearch] = React.useState<string | null>(null);
  const [currentTags, setCurrentTags] = React.useState<string[]>([]);
  const [allTags, setAllTags] = React.useState<string[]>([]);
  const prevSearchRef = React.useRef<string | null>(null);
  const prevTagsRef = React.useRef<string>('');
  const hasLoadedRef = React.useRef<boolean>(false);
  const prevUserStateRef = React.useRef<UserState>(userState);
  const latestBookmarksRequestRef = React.useRef(0);

  /*
    if there is user and the bookmarks collection is empty, fetch the first page
    if there is user and the bookmarks collection is not empty and the target search has changed, 
    reset and fetch the first page of bookmarks according to the target criteria
    */
  React.useEffect(() => {
    // Reset hasLoadedRef when user logs in (transitions to LOGGED_IN)
    if (prevUserStateRef.current !== UserState.LOGGED_IN && userState === UserState.LOGGED_IN) {
      hasLoadedRef.current = false;
    }
    prevUserStateRef.current = userState;

    // Don't try to fetch if user is not logged in
    if (userState !== UserState.LOGGED_IN && userState !== UserState.CHECKING_TOKEN && userState !== UserState.CHECK_TOKEN) {
      return;
    }

    const searchChanged = prevSearchRef.current !== targetSearch;
    const tagsChanged = prevTagsRef.current !== targetTags.join(',');
    const needsInitialLoad = !hasLoadedRef.current;
    const shouldFetch = !loadingBookmarks && (needsInitialLoad || targetNotConsistent() || searchChanged || tagsChanged);

    if (shouldFetch) {
      const requestId = latestBookmarksRequestRef.current + 1;
      latestBookmarksRequestRef.current = requestId;
      prevSearchRef.current = targetSearch;
      prevTagsRef.current = targetTags.join(',');
      setLoadingBookmarks(true);
      setNextToken(undefined); // Reset pagination
      // Clear bookmarks immediately when search/tags change to show loading state
      if (searchChanged || tagsChanged) {
        setAllBookmarks(null);
      }
      (async () => {
        const checkedUser = await checkToken();
        if (checkedUser) {
          // Only mark as loaded after we successfully get a user
          hasLoadedRef.current = true;
          const fetchedBookmarks = await getAllBookmarks(
            checkedUser,
            targetSearch || undefined,
            undefined, // Start from beginning
            targetTags.length > 0 ? targetTags : undefined,
          );
          if (latestBookmarksRequestRef.current !== requestId) {
            return;
          }
          setLoadedSearch(targetSearch);
          setLoadedTags(targetTags);
          setCurrentSearch(targetSearch);
          setCurrentTags(targetTags);
          if (fetchedBookmarks.result) {
            setAllBookmarks(fetchedBookmarks.result.bookmarks);
            setNextToken(fetchedBookmarks.result.nextToken);
            // Only show message if there's a search or tags - otherwise infinite scroll handles it silently
            if (targetSearch || targetTags.length > 0) {
              showMessage(`${fetchedBookmarks.result.bookmarks.length} bookmarks found`);
            }
          }
          else {
            showErrorMessage('Failed to fetch bookmarks');
            setAllBookmarks([]);
            setNextToken(undefined);
          }
          // Fetch tags on initial load
          if (!searchChanged && !tagsChanged) {
            const tags = await getAllTags(checkedUser);
            setAllTags(tags);
          }
        } else {
          // User not available yet, reset hasLoadedRef so we retry when user becomes available
          hasLoadedRef.current = false;
        }
        if (latestBookmarksRequestRef.current === requestId) {
          setLoadingBookmarks(false);
        }
      })();
    }

  }, [checkToken, loadingBookmarks, setLoadedSearch, setLoadedTags, setLoadingBookmarks, showErrorMessage, showMessage, targetNotConsistent, targetSearch, targetTags, userState, user]);

  const loadMoreBookmarks = React.useCallback(async () => {
    if (isLoadingMore || !nextToken) {
      return;
    }

    setIsLoadingMore(true);
    const checkedUser = await checkToken();
    if (checkedUser) {
      const fetchedBookmarks = await getAllBookmarks(
        checkedUser,
        currentSearch || undefined,
        nextToken,
        currentTags.length > 0 ? currentTags : undefined,
      );
      if (fetchedBookmarks.result) {
        setAllBookmarks((prev) => {
          if (!prev) return fetchedBookmarks.result!.bookmarks;
          return [...prev, ...fetchedBookmarks.result!.bookmarks];
        });
        setNextToken(fetchedBookmarks.result.nextToken);
      }
      else {
        showErrorMessage('Failed to load more bookmarks');
      }
    }
    setIsLoadingMore(false);
  }, [checkToken, currentSearch, currentTags, isLoadingMore, nextToken, showErrorMessage]);

  React.useEffect(() => {
    debuglog('queueProcessingState', queueProcessingState, 'userState', userState);
    if (queueProcessingState === QueueProcessingStates.INITIAL || queueProcessingState === QueueProcessingStates.ADDING_ENTRY) {
      setQueueProcessingState(QueueProcessingStates.LOADING_FROM_STORAGE);
      const existingQueue = localStorage.getItem(QUEUE_KEY);
      debuglog('existingQueue', existingQueue);
      if (existingQueue) {
        const parsed = JSON.parse(existingQueue) as BookmarksQueueEntry[];
        setQueueEntries(parsed);
        if (parsed.length > 0) {
          return setQueueProcessingState(QueueProcessingStates.QUEUE_LOADED);
        }
      }
      else {
        setQueueEntries([]);
      }
      setQueueProcessingState(QueueProcessingStates.IDLE);
    }
    else if (queueProcessingState === QueueProcessingStates.QUEUE_LOADED) {
      if (isProcessingQueueRef.current || userState !== UserState.LOGGED_IN) {
        return;
      }
      isProcessingQueueRef.current = true;
      // Bug 1: snapshot which URLs we're about to process so FINISHED_PROCESSING
      // can detect entries that were added to the queue while we were running
      processingQueueRef.current = new Set(queueEntries.map(e => e.url));
      setQueueProcessingState(QueueProcessingStates.PROCESSING_QUEUE);
      debuglog('processing queue entries', queueEntries);
      (async () => {
        try {
          const checkedUser = await checkToken();
          if (checkedUser) {
            const newQueue: BookmarksQueueEntry[] = [];
            for (const entry of queueEntries) {
              debuglog('processing queue entry', entry);
              if (entry.status !== QueueEntryStates.SAVED && entry.status !== QueueEntryStates.ALREADY_SAVED) {
                // Bug 4: mark as saving so the Share UI reflects progress
                setQueueEntries(prev => prev.map(e => e.url === entry.url ? { ...e, status: QueueEntryStates.SAVING, failureMessage: undefined } : e));
                const result = await stash(checkedUser, entry.url);
                if (result.result) {
                  if (result.result.alreadySaved) {
                    debuglog('bookmark already saved', result.result.bookmark);
                    // Show the entry in the queue with ALREADY_SAVED status so the user
                    // can see which URL matched and what it was saved as
                    const alreadySavedEntry = {
                      ...entry,
                      status: QueueEntryStates.ALREADY_SAVED,
                      existingTitle: result.result.bookmark?.title,
                      failureMessage: undefined
                    };
                    setQueueEntries(prev => prev.map(e => e.url === entry.url ? alreadySavedEntry : e));
                    newQueue.push(alreadySavedEntry);
                  }
                  else {
                    debuglog('saved queued bookmark', result.result.bookmark);
                    setAllBookmarks(prev => prev ? [result.result!.bookmark, ...prev] : [result.result!.bookmark]);
                  }
                }
                else {
                  const failureMessage = formatApiError(result.error, 'Failed to save bookmark');
                  debuglog('failed to save queued bookmark', entry.url, failureMessage);
                  newQueue.push({ ...entry, status: QueueEntryStates.FAILED, failureMessage });
                }
              }
            }
            setQueueEntries(newQueue);
            setQueueProcessingState(QueueProcessingStates.FINISHED_PROCESSING);
          } else {
            // Bug 2: token unavailable — go back to QUEUE_LOADED so the state
            // machine retries automatically when the user logs in
            setQueueProcessingState(QueueProcessingStates.QUEUE_LOADED);
          }
        } finally {
          isProcessingQueueRef.current = false;
        }
      })();
    }
    else if (queueProcessingState === QueueProcessingStates.FINISHED_PROCESSING) {
      // Bug 1: re-read localStorage to pick up any entries that were queued
      // while processing was in flight, then merge with the failed results
      const currentStorageQueue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]') as BookmarksQueueEntry[];
      const addedDuringProcessing = currentStorageQueue.filter(e => !processingQueueRef.current.has(e.url));
      const mergedQueue = [...queueEntries, ...addedDuringProcessing];
      // Don't persist ALREADY_SAVED entries — they're only shown in-session so the user
      // can see what matched, then dismissed. They should not be re-processed on reload.
      const queueToStore = mergedQueue.filter(e => e.status !== QueueEntryStates.ALREADY_SAVED);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queueToStore));
      if (addedDuringProcessing.length > 0) {
        setQueueEntries(mergedQueue);
        setQueueProcessingState(QueueProcessingStates.QUEUE_LOADED);
      } else {
        setQueueProcessingState(QueueProcessingStates.IDLE);
      }
    }
    else {
      debuglog('queueProcessingState', queueProcessingState);
    }
  }, [checkToken, queueEntries, queueProcessingState, showMessage, userState]);

  const removeBookmark = (url: string) => {
    if (allBookmarks) {
      setAllBookmarks(allBookmarks.filter(b => b.url !== url));
    }
  };

  const refreshTags = React.useCallback(async () => {
    const checkedUser = await checkToken();
    if (checkedUser) {
      const tags = await getAllTags(checkedUser);
      setAllTags(tags);
    }
  }, [checkToken]);

  const saveModifiedBookmark = async (bookmarkToSave: Bookmark) => {
    const checkedUser = await checkToken();
    if (checkedUser && userState === UserState.LOGGED_IN && allBookmarks) {
      setSaving(true);
      const updatedBookmark = await updateExistingBookmark(checkedUser, bookmarkToSave);
      if (!updatedBookmark) {
        showErrorMessage(`Failed to update site: ${bookmarkToSave.title}`);
      }
      else {
        showMessage(`Site updated: ${updatedBookmark.title}`);
        const newBookmarks = allBookmarks.map((bookmark) => {
          if (bookmark.fid === updatedBookmark.fid) {
            return updatedBookmark;
          }
          else {
            return bookmark;
          }
        }) as Bookmark[];
        setAllBookmarks(newBookmarks);
        // Refresh tags in case new tags were added or old ones removed
        await refreshTags();
      }
      setSaving(false);
    }

  };

  const saveBulkModifiedBookmarks = async (bookmarkIds: string[], addTags: string[], removeTags: string[]) => {
    const checkedUser = await checkToken();
    if (checkedUser && userState === UserState.LOGGED_IN && allBookmarks) {
      setSaving(true);
      const result = await bulkUpdateBookmarkTags(checkedUser, { bookmarkIds, addTags, removeTags });
      if (!result.result) {
        showErrorMessage('Failed to update selected bookmark tags');
        setSaving(false);
        return false;
      }

      const updatedBookmarksById = new Map(result.result.map(bookmark => [bookmark.fid, bookmark]));
      setAllBookmarks(allBookmarks.map(bookmark => updatedBookmarksById.get(bookmark.fid) || bookmark));
      await refreshTags();
      showMessage(`Updated tags for ${result.result.length} bookmarks`);
      setSaving(false);
      return true;
    }
    return false;
  };

  const saveBookMark = async (urlToSave: string) => {
    const cleanedText = extractUrlFromText(urlToSave);
    const checkedUser = await checkToken();
    if (checkedUser && cleanedText && allBookmarks !== null && userState === UserState.LOGGED_IN) {
      setSaving(true);
      const response = await stash(checkedUser, cleanedText);
      if (!response.result) {
        showErrorMessage(`Failed to save site: ${formatApiError(response.error, cleanedText)}`);
      }
      else if (response.result.alreadySaved) {
        showMessage('Site already saved');
      }
      else {
        showMessage(`Site saved: ${response.result.bookmark.title}`);
        setAllBookmarks([response.result.bookmark, ...allBookmarks]);
      }
      setSaving(false);
    }
  }

  const queueBookmarkForSaving = React.useCallback((url: string) => {
    const existingQueue = localStorage.getItem(QUEUE_KEY) || '[]';
    const queue = JSON.parse(existingQueue) as BookmarksQueueEntry[];
    const existingEntry = queue.find(entry => entry.url === url);
    if (existingEntry) {
      if (existingEntry.status === QueueEntryStates.FAILED) {
        // Bug 3: a previously failed URL should be retried when the user shares
        // it again — reset to PENDING rather than silently dropping it
        existingEntry.status = QueueEntryStates.PENDING;
        delete existingEntry.failureMessage;
        localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
        setQueueProcessingState(QueueProcessingStates.ADDING_ENTRY);
      } else {
        console.log('Already queued', url);
      }
    }
    else {
      console.log('Queuing', url);
      queue.push({ url, status: QueueEntryStates.PENDING });
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      setQueueProcessingState(QueueProcessingStates.ADDING_ENTRY);
    }
  }, []);

  const dequeueBookmark = (url: string) => {
    const existingQueue = localStorage.getItem(QUEUE_KEY) || '[]';
    const queue = JSON.parse(existingQueue) as BookmarksQueueEntry[];
    const newQueue = queue.filter(entry => entry.url !== url);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(newQueue));
    // Update state directly rather than replacing from localStorage, so in-memory-only
    // entries (e.g. ALREADY_SAVED) aren't lost when dismissing a different entry
    setQueueEntries(prev => prev.filter(e => e.url !== url));
  };

  return (
    <DataContext.Provider value={{
      allBookmarks, setAllBookmarks, removeBookmark,
      saveBookMark, saveModifiedBookmark,
      queueBookmarkForSaving, queueEntries, dequeueBookmark,
      loadMoreBookmarks,
      hasMoreBookmarks: !!nextToken,
      isLoadingMore,
      allTags,
      refreshTags,
      saveBulkModifiedBookmarks,
    }}>
      {children}
    </DataContext.Provider>
  );
};
